import {action, computed, makeObservable, observable} from 'mobx';
import {Optional} from 'utility-types';
import {Severity} from '@netx/core-components/Theme';

export const StatusItemStatus = {
	PENDING: 'pending',
	PROCESSING: 'processing',
} as const;
export type StatusItemStatus =
	typeof StatusItemStatus[keyof typeof StatusItemStatus];

export interface IStatusItem<
	D extends object = object,
	S extends string = StatusItemStatus,
> {
	complete: boolean;
	id: string; // immutable
	/** unknown data */
	data?: D;
	error?: string;
	missing?: boolean;
	preview?: string;
	resumable: boolean;
	/** 0 - 100 */
	progress: number;
	status: S; // TODO: define a base set of statuses
	/** generic tasks */
	tasks: string[]; // = observable.array<string>([], {deep: false});
	/** the total number of tasks */
	totalTasks: number;
	paused: boolean;

	// computed (unsettable)
	canPause: boolean;
	canRemove: boolean;
	canResume: boolean;
	label: string;
	severity: Severity;
	viable: boolean;
}

export type StatusItemInitProps<
	D extends object = object,
	S extends string = StatusItemStatus,
> = Optional<
	Omit<
		IStatusItem<D, S>,
		'canPause' | 'canRemove' | 'canResume' | 'label' | 'severity' | 'viable'
	>,
	| 'data'
	| 'tasks'
	| 'totalTasks'
	| 'paused'
	| 'progress'
	| 'resumable'
	| 'status'
	| 'complete'
>;

export class StatusItem<
	D extends object = object,
	S extends string = StatusItemStatus,
> implements IStatusItem<D, S>
{
	id: string; // immutable
	/** unknown data */
	data: D = observable.object<D>({} as D);
	error?: string;
	preview?: string;
	paused = false;
	/** 0 - 100 */
	progress = 0;
	resumable = true;
	status: S = StatusItemStatus.PENDING as S;
	/** generic tasks */
	tasks = observable.array<string>([], {deep: false});
	/** the total number of tasks */
	totalTasks = 0;
	complete = false;
	missing = false;

	/** can item be paused */
	get canPause() {
		return !this.paused && this.viable;
	}

	get canRemove() {
		return true;
	}

	/** can item be resumed */
	get canResume() {
		return this.paused && this.viable;
	}

	get label(): string {
		return this.getStatusLabel(this.status);
	}

	get severity() {
		if (this.missing) return Severity.WARNING;
		if (this.error) return Severity.ERROR;
		if (this.complete) return Severity.SUCCESS;
		return Severity.PRIMARY;
	}

	get viable() {
		return !this.complete && this.resumable && !this.missing;
	}

	constructor(props: StatusItemInitProps<D, S>) {
		this.id = props.id;
		Object.assign(this.data, props.data);
		this.error = props.error;
		this.complete = props.complete === true;
		this.missing = props.missing === true;
		this.paused = props.paused === true;
		this.progress = props.progress || 0;
		this.preview = props.preview;
		this.resumable = props.resumable !== false;
		this.status = props.status || this.status;
		this.totalTasks = props.totalTasks || 0;
		this.tasks.replace(props.tasks || []);

		makeObservable(this, {
			canPause: computed,
			canRemove: computed,
			canResume: computed,
			complete: observable,
			data: observable,
			error: observable,
			label: computed,
			missing: observable,
			paused: observable,
			progress: observable,
			resetError: action,
			resetProgress: action,
			resumable: observable,
			resume: action,
			setComplete: action,
			setData: action,
			setError: action,
			setMissing: action,
			setPaused: action,
			setPreview: action,
			setProgress: action,
			setStatus: action,
			severity: computed,
			status: observable,
			tasks: observable,
			togglePause: action,
			totalTasks: observable,
			viable: computed,
		});
	}

	isInStatuses(statuses: S[]) {
		return statuses.includes(this.status);
	}

	notInStatuses(statuses: S[]) {
		return !this.isInStatuses(statuses);
	}

	resume() {
		return this.setPaused(false);
	}

	getStatusLabel(status?: S) {
		if (status == null) return '';
		return status;
	}

	/** get human readbale task label */
	getTaskLabel(task?: string) {
		return task;
	}

	/** update data */
	setData(data: Partial<D> | ((data: D) => Partial<D>)) {
		Object.assign(
			this.data,
			typeof data === 'function' ? data(this.data) : data,
		);
	}

	/** clear error */
	resetError() {
		this.resumable = true;
		this.error = undefined;
	}

	/** reset progress */
	resetProgress() {
		this.progress = 0;
		this.setTasks([]);
		this.totalTasks = 0;
	}

	setComplete(complete: boolean) {
		this.complete = complete;
		this.setTasks([]);
		this.setProgress(100);
	}

	setError(
		error: string,
		opts: {
			recoverable?: boolean;
			status?: S;
		} = {},
	) {
		const {recoverable, status} = opts;

		this.error = error;
		this.resumable = recoverable === true;
		if (!recoverable) this.setComplete(true);
		this.resetProgress();
		status != null && this.setStatus(status);
	}

	setMissing(missing: boolean) {
		this.missing = missing;
		if (missing) this.paused = true;
	}

	/**
	 * Set paused
	 * @param paused
	 * @returns Whether the item was affected
	 */
	setPaused(paused: boolean) {
		if (paused && !this.canPause) return false;
		if (!paused && !this.canResume) return false;

		this.paused = paused;
		return true;
	}

	setProgress(progress: number) {
		this.progress = progress;
	}

	setPreview(preview?: string) {
		this.preview = preview;
	}

	setStatus(status: S | ((status: S) => S)) {
		this.status = typeof status === 'function' ? status(this.status) : status;
	}

	setTasks(tasks: string[], totalTasks?: number) {
		this.tasks.replace(tasks);
		this.totalTasks = totalTasks != null ? totalTasks : tasks.length;
	}

	togglePause() {
		return this.setPaused(!this.paused);
	}

	toJson() {
		return {
			id: this.id,
			data: this.data ? JSON.parse(JSON.stringify(this.data)) : null,
			error: this.error || null,
			paused: this.paused === true,
			progress: this.progress,
			preview: this.preview || null,
			status: (this.status as string) || null,
			totalTasks: this.totalTasks,
			tasks: [...this.tasks],
		};
	}

	dispose() {}
}

export default StatusItem;
